import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LoadingScreen from "components/LoadingScreen";
import { toast } from "react-toastify";
import { sdkWrapperURL } from "utils/api-url-list";
import { ranges } from "utils/ranges";

const Dashboard = () => {
  const [isLoading, setLoading] = useState(true);
  const [isAnimating, setAnimating] = useState(false);
  const [analytics, setAnalytics] = useState({
    scan: { total: "--", self: "--", others: "--" },
    patient: { total: "--", self: "--", others: "--" },
  });
  const [isGlobal, setGlobal] = useState(false);
  const [recentScanData, setRecentScanData] = useState({});

  useEffect(() => {
    const timer = setInterval(() => setAnimating(a => !a), 750);
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const analyticsResp = await fetch(sdkWrapperURL("/users/sdk/test_api_private"), {
          signal: controller.signal,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
          },
          body: JSON.stringify({
            employee_id: localStorage.getItem("emp_id"),
            action: "analytics",
          }),
        });
        const analyticsRespJson = await analyticsResp.json();
        if (analyticsRespJson.statusCode?.toString().startsWith("2"))
          setAnalytics({
            scan: {
              total: analyticsRespJson.response.scan_count,
              self: analyticsRespJson.response.agent_scan_count,
              others:
                analyticsRespJson.response.scan_count - analyticsRespJson.response.agent_scan_count,
            },
            patient: {
              total: analyticsRespJson.response.patient_count,
              self: analyticsRespJson.response.agent_patient_count,
              others:
                analyticsRespJson.response.patient_count -
                analyticsRespJson.response.agent_patient_count,
            },
          });
        else if (!(analyticsRespJson.statusCode?.toString() === "403"))
          throw new Error(analyticsRespJson.message);
      } catch (err) {
        if (err.name !== "AbortError")
          toast(err.message, {
            type: "error",
            position: "top-center",
            hideProgressBar: false,
            draggable: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            closeOnClick: false,
            autoClose: 3000,
          });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setRecentScanData({});
        const recentScanResp = await fetch(sdkWrapperURL("/users/sdk/test_api_private"), {
          signal: controller.signal,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
          },
          body: JSON.stringify({
            employee_id: localStorage.getItem("emp_id"),
            action: "list_history_scans",
            history: isGlobal ? "global" : "self",
            table_config_rows_per_page: 1,
            table_config_page_no: 1,
          }),
        });
        const recentScanRespJson = await recentScanResp.json();
        if (recentScanRespJson.statusCode?.toString().startsWith("2")) {
          if (recentScanRespJson.response?.length > 0) {
            const { value, ...bp_range } = ranges.BP.reduce((rank, range) =>
              recentScanRespJson.response[0].vitals.bp_sys >= range.value ? range : rank
            );
            recentScanRespJson.response[0].vitals.bp_range = bp_range;
            if (typeof recentScanRespJson.response[0].glucose_info?.hba1c !== "undefined") {
              const { value: _, ...hba1c_range } = ranges.HbA1c.reduce((rank, range) =>
                recentScanRespJson.response[0].glucose_info.hba1c >= range.value ? range : rank
              );
              recentScanRespJson.response[0].glucose_info.hba1c_range = hba1c_range;
            }
            setRecentScanData(recentScanRespJson.response[0]);
          }
        } else if (!(recentScanRespJson.statusCode?.toString() === "403"))
          throw new Error(recentScanRespJson.message);
      } catch (err) {
        if (err.name !== "AbortError")
          toast(err.message, {
            type: "error",
            position: "top-center",
            hideProgressBar: false,
            draggable: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            closeOnClick: false,
            autoClose: 3000,
          });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [isGlobal]);

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <div className="">
      <div className="py-6 text-center">
        <h1 className="text-primary text-2xl font-semibold">Monitor Your Health</h1>
        <p className="mt-2 text-secondary text-lg">Do a health-check in minutes</p>
      </div>
      <Link
        to="/select-option"
        className={`block my-4 mx-auto px-4 py-2 w-48 rounded-full outline-none bg-secondary text-white text-center text-xl font-medium ease-in-out duration-700 ${
          isAnimating ? "scale-110" : "scale-100"
        }`}
      >
        Start a Scan
      </Link>
      <div className="mt-12 grid grid-cols-2 gap-5">
        {[
          { label: "Total Scans Done", ...analytics.scan },
          { label: "No. of Patients", ...analytics.patient },
        ].map((an, index) => (
          <div
            key={`analytics-${index}`}
            className="p-2.5 flex flex-col items-center justify-between gap-3 rounded-lg bg-gradient text-white text-center"
          >
            <div>
              <p className="text-sm">{an.label}</p>
              <p className="mt-0.5 text-xl font-semibold">{an.total}</p>
            </div>
            <div>
              <p>
                <span className="text-xs">Self:</span>&nbsp;
                <span className="text-base font-semibold">{an.self}</span>
              </p>
              <p>
                <span className="text-xs">Others:</span>&nbsp;
                <span className="text-base font-semibold">{an.others}</span>
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h3 className="text-primary text-xl font-semibold">Most Recent Scan</h3>
          <div className="flex p-1 space-x-1 rounded-lg bg-gradient text-xs">
            <button
              type="button"
              className={`px-3 py-1.5 rounded-md ${
                isGlobal ? "text-white" : "bg-white text-secondary font-medium"
              }`}
              onClick={() => setGlobal(false)}
            >
              Self
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 rounded-md ${
                !isGlobal ? "text-white" : "bg-white text-secondary font-medium"
              }`}
              onClick={() => setGlobal(true)}
            >
              All
            </button>
          </div>
        </div>
        {recentScanData.scan_id?.length > 0 ? (
          <div className="mt-4 p-4 rounded-md bg-gradient text-white">
            <div className="grow flex justify-between">
              <div className="overflow-hidden">
                <p className="text-white/70 text-xs font-light">
                  {recentScanData.created_at?.length > 0
                    ? new Date(recentScanData.created_at).toLocaleString("en", {
                        dateStyle: "long",
                        timeStyle: "medium",
                      })
                    : "--"}
                </p>
                <p className="text-xl font-medium capitalize truncate">
                  {recentScanData.basic_info?.patient_id ?? "--"}
                </p>
              </div>
              <div className="flex flex-col items-center justify-between gap-1.5">
                <svg className="shrink-0 h-9" fill="currentColor" viewBox="0 0 400 400">
                  {recentScanData.device?.split("_")[2] === "face" ? (
                    <path d="M102.947 1.832 C 89.883 6.284,79.885 17.743,77.356 31.161 C 76.261 36.970,76.261 363.030,77.356 368.839 C 79.922 382.454,90.517 394.366,103.676 398.430 C 111.631 400.887,288.369 400.887,296.324 398.430 C 309.483 394.366,320.078 382.454,322.644 368.839 C 323.160 366.101,323.438 320.697,323.438 238.920 L 323.438 113.210 321.520 111.293 C 318.983 108.756,315.392 108.756,312.855 111.293 L 310.938 113.210 310.938 230.824 L 310.938 348.438 231.214 348.438 L 151.491 348.438 149.574 350.355 C 147.037 352.892,147.037 356.483,149.574 359.020 L 151.491 360.938 231.321 360.938 L 311.150 360.938 310.650 364.585 C 309.023 376.453,299.558 385.772,287.335 387.539 C 280.923 388.467,119.077 388.467,112.665 387.539 C 100.442 385.772,90.977 376.453,89.350 364.585 L 88.850 360.938 108.914 360.938 L 128.977 360.938 130.895 359.020 C 133.432 356.483,133.432 352.892,130.895 350.355 L 128.977 348.438 109.020 348.438 L 89.063 348.438 89.063 191.939 C 89.063 19.764,88.722 31.663,93.865 23.900 C 96.698 19.623,101.872 15.540,107.031 13.510 C 111.099 11.909,275.948 10.903,286.838 12.412 C 296.783 13.790,304.374 19.252,308.461 27.971 L 310.547 32.422 310.938 61.443 C 311.379 94.272,311.277 93.750,317.252 93.750 C 323.413 93.750,323.438 93.622,323.438 61.778 C 323.438 23.680,321.145 16.321,306.159 6.313 C 296.063 -0.429,303.271 -0.003,199.722 0.020 L 108.203 0.040 102.947 1.832 M179.688 22.120 C 164.441 26.953,164.789 51.509,180.169 56.076 C 184.560 57.380,215.440 57.380,219.831 56.076 C 235.516 51.418,235.516 26.707,219.831 22.049 C 215.699 20.821,183.604 20.878,179.688 22.120 M217.614 35.511 C 224.063 41.961,219.045 44.531,200.000 44.531 C 182.899 44.531,180.469 43.851,180.469 39.063 C 180.469 38.164,181.332 36.566,182.386 35.511 C 185.390 32.508,214.610 32.508,217.614 35.511 M108.324 87.366 C 105.969 89.968,106.420 124.154,108.839 126.426 C 111.238 128.680,114.448 128.592,116.832 126.207 C 118.723 124.317,118.750 124.094,118.750 110.582 L 118.750 96.875 132.248 96.875 C 147.905 96.875,149.219 96.458,149.219 91.483 C 149.219 86.052,148.780 85.938,127.989 85.938 C 111.183 85.938,109.507 86.059,108.324 87.366 M249.730 87.366 C 247.880 89.411,248.088 93.363,250.147 95.282 C 251.734 96.760,252.849 96.875,265.577 96.874 L 279.297 96.873 279.055 108.397 C 278.922 114.736,279.030 121.084,279.294 122.505 C 280.288 127.845,285.538 129.923,288.926 126.317 C 291.356 123.730,291.593 89.400,289.196 87.230 C 286.920 85.170,251.608 85.292,249.730 87.366 M185.488 100.027 C 180.246 102.067,175.429 106.295,172.565 111.370 C 170.328 115.333,170.014 115.563,164.610 117.197 C 144.138 123.385,130.017 137.473,124.905 156.807 C 123.246 163.083,122.452 182.697,123.707 186.432 C 124.189 187.869,123.898 188.167,121.598 188.599 C 94.641 193.656,101.249 251.693,128.669 250.699 L 133.828 250.512 135.519 255.530 C 156.184 316.875,245.822 316.349,264.875 254.771 L 266.192 250.513 271.341 250.700 C 297.650 251.653,305.604 198.229,280.482 189.307 L 276.863 188.021 277.194 179.362 C 277.731 165.315,274.795 151.991,269.089 142.578 L 266.247 137.891 267.694 133.594 C 272.033 120.709,266.781 107.068,255.109 100.907 L 251.172 98.828 220.313 98.656 C 191.210 98.494,189.227 98.572,185.488 100.027 M250.061 112.714 C 256.228 116.869,258.238 123.026,255.448 129.213 C 252.183 136.455,252.423 140.272,256.546 146.646 C 266.064 161.360,267.299 181.877,259.961 203.400 C 256.990 212.116,256.081 209.411,255.861 191.201 C 255.475 159.271,250.133 153.721,225.670 159.835 C 208.005 164.250,196.057 164.433,179.297 160.548 C 162.632 156.685,159.709 156.431,155.671 158.491 C 147.771 162.521,143.819 177.087,145.068 197.577 C 146.104 214.581,140.072 207.269,136.700 187.434 C 131.480 156.724,143.456 136.409,171.641 128.165 C 177.182 126.544,182.031 123.088,182.031 120.760 C 182.031 118.906,186.502 113.581,189.156 112.274 C 193.909 109.933,246.498 110.313,250.061 112.714 M170.171 171.435 C 192.398 177.031,207.387 177.257,227.716 172.303 C 243.328 168.499,243.355 168.537,243.358 194.206 L 243.359 209.896 245.480 212.958 C 246.647 214.642,249.371 217.764,251.535 219.895 L 255.469 223.769 255.469 231.848 C 255.469 248.605,250.281 261.587,239.164 272.647 C 203.551 308.077,144.531 282.903,144.531 232.283 L 144.531 224.191 150.708 218.089 C 158.059 210.826,158.494 209.608,157.730 198.438 C 156.966 187.260,158.045 176.118,160.359 171.289 C 161.394 169.130,160.989 169.124,170.171 171.435 M170.782 187.603 C 163.522 188.629,160.769 191.421,162.037 196.474 C 162.923 200.005,165.756 201.120,171.447 200.177 C 174.544 199.664,177.557 199.615,179.423 200.048 C 186.223 201.626,190.625 199.540,190.625 194.741 C 190.625 188.875,182.493 185.950,170.782 187.603 M221.875 187.172 C 221.230 187.298,218.989 187.627,216.894 187.904 C 209.885 188.829,207.019 194.590,211.393 198.964 C 213.269 200.840,213.720 200.939,217.838 200.377 C 223.527 199.602,224.487 199.599,229.834 200.336 C 236.707 201.284,240.719 196.016,237.081 190.821 C 235.539 188.621,226.084 186.352,221.875 187.172 M128.784 206.250 C 132.147 216.706,133.428 236.797,130.795 237.807 C 119.264 242.232,111.462 212.501,121.661 202.996 C 125.965 198.985,126.530 199.243,128.784 206.250 M278.414 203.019 C 288.375 213.416,280.746 242.236,269.205 237.807 C 266.267 236.680,267.844 219.423,271.982 207.422 C 272.870 204.844,273.726 202.295,273.884 201.758 C 274.355 200.148,276.139 200.645,278.414 203.019 M168.750 212.500 C 166.470 214.780,166.496 223.351,168.789 225.426 C 174.434 230.535,181.583 223.344,178.910 215.245 C 177.447 210.813,171.928 209.322,168.750 212.500 M223.301 211.749 C 220.006 213.671,219.647 222.667,222.752 225.477 C 227.390 229.675,232.813 226.194,232.813 219.020 C 232.813 211.991,228.515 208.706,223.301 211.749 M192.276 239.648 C 186.216 244.680,191.230 251.097,200.879 250.658 C 209.661 250.260,213.479 244.800,208.348 239.980 C 205.916 237.695,194.902 237.468,192.276 239.648 M183.949 255.824 C 174.410 265.363,198.001 274.237,212.986 266.747 C 223.090 261.696,218.299 250.883,207.933 255.342 C 203.037 257.449,198.099 257.490,192.782 255.469 C 187.474 253.452,186.273 253.500,183.949 255.824 M108.730 265.871 C 106.300 268.457,106.063 302.788,108.460 304.957 C 109.704 306.083,112.262 306.250,128.261 306.250 C 148.905 306.250,149.219 306.165,149.219 300.569 C 149.219 295.749,147.809 295.313,132.248 295.313 L 118.750 295.313 118.750 281.605 C 118.750 268.093,118.723 267.870,116.832 265.980 C 114.372 263.519,110.982 263.474,108.730 265.871 M281.294 265.581 C 279.146 267.729,278.781 270.697,279.055 283.790 L 279.297 295.315 265.577 295.314 C 250.024 295.312,248.438 295.846,248.438 301.081 C 248.438 306.078,249.145 306.250,269.667 306.250 C 286.473 306.250,288.149 306.128,289.332 304.821 C 291.687 302.219,291.236 268.034,288.817 265.761 C 286.574 263.654,283.299 263.576,281.294 265.581" />
                  ) : (
                    <path d="M102.947 1.832 C 89.883 6.284,79.885 17.743,77.356 31.161 C 76.261 36.970,76.261 363.030,77.356 368.839 C 79.922 382.454,90.517 394.366,103.676 398.430 C 111.631 400.887,288.369 400.887,296.324 398.430 C 309.483 394.366,320.078 382.454,322.644 368.839 C 323.160 366.101,323.438 320.697,323.438 238.920 L 323.438 113.210 321.520 111.293 C 318.983 108.756,315.392 108.756,312.855 111.293 L 310.938 113.210 310.938 230.824 L 310.938 348.438 231.214 348.438 L 151.491 348.438 149.574 350.355 C 147.037 352.892,147.037 356.483,149.574 359.020 L 151.491 360.938 231.321 360.938 L 311.150 360.938 310.650 364.585 C 309.023 376.453,299.558 385.772,287.335 387.539 C 280.923 388.467,119.077 388.467,112.665 387.539 C 100.442 385.772,90.977 376.453,89.350 364.585 L 88.850 360.938 108.914 360.938 L 128.977 360.938 130.895 359.020 C 133.432 356.483,133.432 352.892,130.895 350.355 L 128.977 348.438 109.020 348.438 L 89.063 348.438 89.063 191.939 C 89.063 19.764,88.722 31.663,93.865 23.900 C 96.698 19.623,101.872 15.540,107.031 13.510 C 111.099 11.909,275.948 10.903,286.838 12.412 C 296.783 13.790,304.374 19.252,308.461 27.971 L 310.547 32.422 310.938 61.443 C 311.379 94.272,311.277 93.750,317.252 93.750 C 323.413 93.750,323.438 93.622,323.438 61.778 C 323.438 23.680,321.145 16.321,306.159 6.313 C 296.063 -0.429,303.271 -0.003,199.722 0.020 L 108.203 0.040 102.947 1.832 M124.297 38.577 C 123.677 39.325,123.468 44.279,123.616 54.759 L 123.828 69.863 125.977 71.065 C 127.909 72.145,128.341 72.145,130.273 71.065 L 132.422 69.864 132.638 58.370 L 132.854 46.875 143.806 46.875 C 157.545 46.875,161.513 44.318,156.840 38.477 C 155.654 36.994,125.533 37.088,124.297 38.577 M239.871 39.011 C 236.633 45.063,239.727 46.875,253.299 46.875 L 264.063 46.875 264.063 58.159 C 264.063 68.934,264.141 69.498,265.799 70.659 C 268.110 72.278,270.561 72.196,272.145 70.446 C 273.791 68.627,274.109 40.046,272.500 38.438 C 270.699 36.637,240.861 37.161,239.871 39.011 M189.844 43.777 C 173.979 46.595,157.320 60.334,162.946 65.960 C 165.194 68.207,167.886 67.420,172.547 63.154 C 193.065 44.372,225.459 52.721,234.070 79.010 C 235.885 84.550,235.938 85.433,235.938 110.252 L 235.938 135.793 231.640 136.061 L 227.342 136.328 227.343 128.686 C 227.344 120.526,226.322 116.753,223.205 113.411 C 215.114 104.737,198.617 107.506,194.579 118.215 C 194.062 119.586,193.750 129.192,193.750 143.738 L 193.750 167.062 190.854 166.262 C 189.262 165.822,186.537 165.654,184.800 165.888 C 177.171 166.916,166.075 155.193,162.407 142.231 C 160.938 137.041,160.630 99.781,161.881 78.540 C 162.308 71.281,154.605 71.262,152.466 78.517 C 150.631 84.738,150.229 132.313,151.939 140.890 C 154.148 151.975,160.126 162.396,167.738 168.434 C 169.798 170.069,171.781 171.650,172.143 171.948 C 172.505 172.246,172.154 173.477,171.362 174.684 C 169.329 177.784,168.825 210.785,170.626 222.852 C 173.023 238.903,179.996 255.796,187.414 263.521 C 193.309 269.660,193.757 270.910,193.718 281.106 L 193.686 289.453 190.007 291.196 C 183.366 294.341,182.813 296.018,182.813 312.983 C 182.812 334.607,184.116 336.120,202.443 335.764 L 213.614 335.547 214.799 333.434 C 217.394 328.807,214.329 326.992,203.482 326.732 C 198.305 326.608,193.787 326.136,193.213 325.660 C 191.586 324.309,191.764 302.097,193.415 300.446 C 195.281 298.580,283.625 298.580,285.491 300.446 C 287.158 302.114,287.319 324.311,285.674 325.676 C 284.958 326.270,275.307 326.608,255.094 326.748 L 225.559 326.953 223.928 329.137 C 222.422 331.156,222.388 331.482,223.483 333.434 L 224.669 335.547 256.173 335.751 C 297.951 336.022,296.094 337.064,296.094 313.346 C 296.094 296.390,294.378 291.989,287.049 290.149 C 283.863 289.350,286.174 277.596,290.954 270.295 C 297.389 260.466,297.534 254.660,291.345 254.699 C 288.574 254.717,287.819 255.424,285.076 260.575 C 278.742 272.470,277.282 276.048,276.008 282.784 L 274.674 289.844 238.899 289.844 L 203.125 289.844 203.121 279.883 C 203.115 266.758,202.599 265.163,196.150 258.347 C 190.029 251.878,189.598 251.120,185.183 239.075 C 180.418 226.077,179.690 220.730,179.689 198.738 C 179.687 176.709,179.954 175.781,186.298 175.781 C 193.141 175.781,193.302 176.219,193.750 196.053 C 194.186 215.335,194.287 215.641,200.000 214.871 C 202.643 214.515,202.735 213.100,203.125 167.080 L 203.516 120.955 205.360 119.462 C 207.838 117.456,212.495 117.516,215.129 119.588 L 217.188 121.207 217.188 159.858 L 217.188 198.509 219.105 200.426 C 221.445 202.766,222.618 202.822,225.285 200.725 L 227.344 199.105 227.344 173.893 C 227.344 144.817,227.121 145.703,234.429 145.703 C 241.196 145.703,240.959 144.731,241.406 174.275 C 241.830 202.277,241.697 201.525,246.233 201.551 C 250.593 201.575,250.781 200.749,250.781 181.525 L 250.781 163.942 253.163 161.561 C 255.272 159.451,255.918 159.230,258.819 159.619 C 264.828 160.425,264.844 160.490,264.844 184.888 L 264.844 206.321 266.761 208.239 C 272.155 213.633,275.000 209.480,275.000 196.211 C 275.000 182.949,275.429 182.031,281.628 182.031 C 289.264 182.031,289.063 181.140,289.063 214.828 C 289.063 246.381,289.018 246.094,293.886 246.094 C 298.521 246.094,298.511 246.167,298.268 212.286 L 298.047 181.393 295.658 178.392 C 291.803 173.549,287.425 171.698,280.743 172.087 L 275.158 172.411 274.873 166.870 C 274.285 155.430,269.047 150.391,257.745 150.391 C 250.986 150.391,250.781 150.338,250.781 148.581 C 250.781 146.472,249.625 144.021,247.552 141.740 C 246.175 140.223,246.068 138.258,245.874 110.880 C 245.678 83.107,245.581 81.386,243.948 76.563 C 236.101 53.385,213.353 39.601,189.844 43.777 M191.406 60.067 C 180.291 62.567,170.513 72.685,168.066 84.218 C 166.659 90.847,166.918 133.525,168.403 139.826 C 171.008 150.878,178.510 158.209,182.813 153.906 C 184.927 151.792,184.777 149.503,182.312 146.271 C 177.607 140.102,177.344 138.252,177.344 111.349 C 177.344 88.717,177.467 86.449,178.901 82.615 C 183.846 69.399,201.487 65.031,212.119 74.390 C 217.695 79.299,219.531 84.057,219.531 93.598 C 219.531 102.594,220.475 104.688,224.533 104.688 C 228.800 104.688,229.688 102.924,229.688 94.439 C 229.688 71.005,212.277 55.373,191.406 60.067 M192.578 76.889 C 185.493 80.051,183.594 84.984,183.594 100.229 C 183.594 113.290,184.401 115.625,188.918 115.625 C 193.077 115.625,193.750 113.525,193.750 100.540 C 193.750 87.126,194.199 85.677,198.353 85.677 C 202.052 85.677,202.691 86.870,203.167 94.663 C 203.715 103.636,206.474 106.242,211.613 102.642 C 214.472 100.640,213.873 86.823,210.731 82.295 C 206.674 76.445,198.815 74.105,192.578 76.889 M124.730 160.804 C 122.834 162.899,122.791 190.648,124.681 192.539 C 125.750 193.607,128.088 193.753,141.273 193.571 L 156.621 193.359 157.693 191.121 C 160.109 186.077,157.496 184.800,144.253 184.549 L 132.813 184.333 132.813 173.147 C 132.813 163.302,132.641 161.806,131.384 160.668 C 129.497 158.960,126.340 159.025,124.730 160.804" />
                  )}
                </svg>
                <p className="text-[10px] leading-none capitalize">
                  {recentScanData.device?.split("_")[2]}
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
              {recentScanData.basic_info?.name && (
                <p className="col-span-full truncate">
                  <strong>Name:</strong>&nbsp;
                  <span className="capitalize">{recentScanData.basic_info.name}</span>
                </p>
              )}
              <p className="truncate">
                <strong>Gender:</strong>&nbsp;
                <span className="capitalize">{recentScanData.basic_info?.gender ?? "--"}</span>
              </p>
              <p className="truncate">
                <strong>Age:</strong>&nbsp;
                <span>{recentScanData.basic_info?.age ?? "--"}</span>
              </p>
              <p className="truncate">
                <strong>Height:</strong>&nbsp;
                <span>
                  {recentScanData.basic_info?.height ?? "--"}&nbsp;
                  <span className="text-xs font-light">cms</span>
                </span>
              </p>
              <p className="truncate">
                <strong>Weight:</strong>&nbsp;
                <span>
                  {recentScanData.basic_info?.weight ?? "--"}&nbsp;
                  <span className="text-xs font-light">kgs</span>
                </span>
              </p>
              <p className="truncate">
                <strong>Heart Rate:</strong>&nbsp;
                <span>
                  {recentScanData.vitals?.heart_rate ?? "--"}&nbsp;
                  <span className="text-xs font-light">bpm</span>
                </span>
              </p>
              <p className="truncate">
                <strong>Resp Rate:</strong>&nbsp;
                <span>
                  {recentScanData.vitals?.resp_rate ?? "--"}
                  <span className="text-xs font-light">/min</span>
                </span>
              </p>
              <p className="truncate">
                <strong>Oxygen Sat:</strong>&nbsp;
                <span>
                  {recentScanData.vitals?.oxy_sat_prcnt ?? "--"}
                  <span className="text-xs font-light">%</span>
                </span>
              </p>
              <p className="truncate">
                <strong>HbA1c:</strong>&nbsp;
                <span className="text-xs">{recentScanData.glucose_info?.hba1c_range.label}</span>
                &nbsp;(
                <span
                  style={{ color: recentScanData.glucose_info?.hba1c_range.color, fontWeight: 500 }}
                >
                  {recentScanData.glucose_info?.hba1c ?? "--"}
                </span>
                )
              </p>
              <p className="truncate">
                <strong>SDNN:</strong>&nbsp;
                <span>
                  {recentScanData.heart_scores?.sdnn?.toFixed?.(2) ?? "--"}&nbsp;
                  <span className="text-xs font-light">msec</span>
                </span>
              </p>
              <p className="truncate">
                <strong>RMSSD:</strong>&nbsp;
                <span>
                  {recentScanData.heart_scores?.rmssd?.toFixed?.(2) ?? "--"}&nbsp;
                  <span className="text-xs font-light">msec</span>
                </span>
              </p>
              <p className="col-span-2 truncate">
                <strong>BP:</strong>&nbsp;
                {recentScanData.vitals?.bp_sys ?? "--"}/{recentScanData.vitals?.bp_dia ?? "--"}
                &nbsp;<span className="text-xs font-light">mmHg</span>&nbsp;(
                {recentScanData.vitals?.bp_range.icon}&nbsp;
                <span className="text-xs font-light">
                  Trend:&nbsp;
                  <span style={{ color: recentScanData.vitals?.bp_range.color, fontWeight: 500 }}>
                    {recentScanData.vitals?.bp_range.label}
                  </span>
                </span>
                &nbsp;)
              </p>
            </div>
            <div className="mt-6 space-y-4">
              <Link
                to={"/update/patient-info/" + recentScanData.basic_info?.patient_id}
                state={{
                  patientData: {
                    patientID: recentScanData.basic_info?.patient_id ?? "",
                    name: recentScanData.basic_info?.name ?? "",
                    age: recentScanData.basic_info?.age ?? "",
                    gender: recentScanData.basic_info?.gender ?? "",
                    height: recentScanData.basic_info?.height ?? "",
                    weight: recentScanData.basic_info?.weight ?? "",
                    waist: recentScanData.basic_info?.waist ?? "",
                  },
                }}
                className="px-4 py-2 flex items-center justify-center rounded-full bg-secondary/80 text-white text-xs text-center font-medium"
              >
                Edit Basic Info
              </Link>
              <Link
                to={"/update/observations/" + recentScanData.scan_id}
                state={{
                  scan_id: recentScanData.scan_id,
                  date: recentScanData.created_at ?? "",
                  patientID: recentScanData.basic_info?.patient_id ?? "",
                  observations: recentScanData.observations ?? [],
                }}
                className="px-4 py-2 flex items-center justify-center rounded-full bg-secondary/80 text-white text-xs text-center font-medium"
              >
                Edit Observations
              </Link>
              <Link
                to="/select-option"
                replace
                state={{
                  patientData: {
                    patientID: recentScanData.basic_info?.patient_id ?? "",
                    name: recentScanData.basic_info?.name ?? "",
                    age: recentScanData.basic_info?.age ?? "",
                    gender: recentScanData.basic_info?.gender ?? "",
                    height: recentScanData.basic_info?.height ?? "",
                    weight: recentScanData.basic_info?.weight ?? "",
                    waist: recentScanData.basic_info?.waist ?? "",
                  },
                }}
                className="px-4 py-2 flex items-center justify-center rounded-full bg-secondary/80 text-white text-xs text-center font-medium"
              >
                Scan Again
              </Link>
            </div>
          </div>
        ) : (
          <p className="mt-4 p-4 rounded-md bg-gradient text-white text-center">
            No Data to show...
          </p>
        )}
        <Link
          to="/history"
          className="block mt-6 text-primary text-center underline underline-offset-4"
        >
          View Full History
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
